import type { ExpressionSpecification } from "maplibre-gl";
import { useAgglomeration } from "@/store/agglomeration";
import { useLocationStore } from "@/store/userPosition";

export function onlyNumbers(value: string, allowedChars = ""): string {
  const reg = new RegExp(`[^0-9${allowedChars}]`, "g");
  return value.trim().replace(reg, "");
}

export function isEmailValid(email: string): boolean {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return re.test(email);
}

export const PURPLE_SCALE = [
  "#FFF0FD",
  "#FFEDFE",
  "#FFC4FD",
  "#FF9CFF",
  "#FA73FF",
  "#F349FF",
] as const;

export const EDA_SCALE = [
  "#FFF1C7",
  "#FFDF8A",
  "#FFC95C",
  "#FFB12B",
  "#FF981A",
  "#E56D10",
] as const;

export const KUPER_SCALE = [
  "#E8F8D8",
  "#C6F3A2",
  "#9CEB67",
  "#55D532",
  "#21A038",
  "#007A3D",
] as const;

function mapStopsToRange(
  dataMin: number,
  dataMax: number,
  stops: number[]
): number[] {
  const origMin = Math.min(...stops);
  const origMax = Math.max(...stops);
  const origRange = origMax - origMin;
  const dataRange = dataMax - dataMin;

  return stops.map((stop) => {
    const normalized = (stop - origMin) / origRange;
    return dataMin + normalized * dataRange;
  });
}

export function makeStepExpression(
  field: string,
  min: number,
  max: number,
  colors: readonly string[],
  stops: number[],
  absolute: string
) {
  if (min === max) {
    return ["case", ["==", ["get", field], null], colors[0], colors[0]];
  }

  const mappedStops =
    absolute === "absolute" ? stops : mapStopsToRange(min, max, stops);
  const stopPairs = mappedStops.flatMap((s, i) => [s, colors[i]]);

  return [
    "case",
    ["==", ["get", field], null],
    colors[0],
    ["interpolate", ["linear"], ["get", field], ...stopPairs],
  ];
}

export function makeAgglomerationColorExpression(
  priceMap: Record<number, number>,
  field: string,
  min: number,
  max: number,
  stops: number[]
): ExpressionSpecification {
  const mappedStops = mapStopsToRange(min, max, stops);

  const stopPairs = mappedStops.flatMap((stop, i) => [stop, PURPLE_SCALE[i]]);

  return ([
    "interpolate",
    ["linear"],

    [
      "match",
      ["get", field],
      ...Object.entries(priceMap).flatMap(([id, price]) => [Number(id), price]),
      0,
    ],

    ...stopPairs,
  ] as unknown) as ExpressionSpecification;
}

export function makeConditionalStepExpression(
  field: string,
  rangesByHexgroupId: Record<number, { min: number; max: number }>,
  colors: readonly string[],
  stops: number[],
  absolute: string,
  fallbackColor: string
): any {
  return [
    "match",
    ["get", "hexgroup_id"],

    ...Object.entries(rangesByHexgroupId).flatMap(([id, { min, max }]) => [
      Number(id),
      makeStepExpression(field, min, max, colors, stops, absolute),
    ]),

    fallbackColor,
  ];
}

export function makeNavigationUrl(lat: number, lng: number): string {
  const web = `https://yandex.ru/maps/?rtext=~${lat},${lng}&rtt=auto`;
  if (/iPhone|iPad|iPod/.test(navigator.platform)) {
    return `maps://?daddr=${lat},${lng}`;
  }

  if (/Android/.test(navigator.userAgent)) {
    return `geo:${lat},${lng}?q=${lat},${lng}`;
  }

  return web;
}

export function openExternalLink(url: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";

  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function expandBBox(
  bbox: [number, number, number, number],
  distanceKm: number
): [number, number, number, number] | undefined {
  if (!bbox || bbox.length !== 4) return undefined;
  const [minLon, minLat, maxLon, maxLat] = bbox;

  const R = 6371;

  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;

  const deltaLat = toDeg(distanceKm / R);

  const centerLat = (minLat + maxLat) / 2;
  const deltaLon = toDeg(distanceKm / (R * Math.cos(toRad(centerLat))));

  return [
    minLon - deltaLon, // new west
    minLat - deltaLat, // new south
    maxLon + deltaLon, // new east
    maxLat + deltaLat, // new north
  ];
}

export function hexToRgba(hex: string, opacity: number = 100): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
        result[3],
        16
      )}, ${opacity / 100})`
    : "rgba(0, 0, 0, 1)";
}

export function isLocationInsideCity(
  lon: number,
  lat: number,
  city?: [number, number, number, number]
): boolean {
  if (!city) return false;
  const [minLon, minLat, maxLon, maxLat] = city;
  return lon >= minLon && lon <= maxLon && lat >= minLat && lat <= maxLat;
}

export const initialLocationCenter: () => [number, number] | null = () => {
  const { isLocationAllowed, location } = useLocationStore.getState();
  if (
    isLocationAllowed &&
    location?.longitude != null &&
    location?.latitude != null &&
    isLocationInsideCity(
      location.longitude,
      location.latitude,
      useAgglomeration.getState().selectedAgglomeration?.bounds
    )
  ) {
    return [location.longitude, location.latitude];
  }
  return null;
};

const testersList = [
  "sweet_men",
  "ustal0st",
  "Vladislav_Orailo",
  "Invrain",
  "Paha872",
  "avtandiili",
  "Xoyopta",
  "Artemonim",
  "Neizvestnuy17",
  "lobanovlaw",
  "yfTimofey",
  "toren332",
  "sev_Tigrov",
  "kakto_vlom",
  "Enotovidniy",
  "RBAARTI",
  "rescue0",
  "see_zzee72",
  "Dyrhlahok",
  "L_eksey",
  "Igor_Ventura",
  "MIRSONOV",
  "Simon000012",
  "killsantori",
  "VSSV07",
  "BigKefSupport",
];

export function isTester(username: string) {
  return testersList.includes(username);
}

function toSnakeCase(str: string) {
  return str
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s\-:.()]+/g, "_")
    .toUpperCase()
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

export function parseErrorCode(raw: string, errorName?: string): string {
  const msg = raw || "";
  const namePart = errorName || "Error";
  const baseCode = toSnakeCase(namePart);

  const m1 = msg.match(/reading ['"]([^'"]+)['"]/i);
  if (m1) {
    return `${baseCode}_READING_${toSnakeCase(m1[1])}`;
  }

  if (/fetch/i.test(msg)) {
    if (/range/i.test(msg)) return "FAILED_TO_FETCH_RANGES";
    return "FETCH_FAILED";
  }

  const m2 = msg.match(/is not a function/i);
  if (m2) {
    return `${baseCode}_NOT_A_FUNCTION`;
  }

  return baseCode;
}
